import { fetchEntity, fetchEntityList, fetchProjectLookup } from '@/lib/entity-api';
import { getApiUrl } from '@/lib/api-config';
import type { BidItem, Project } from '../types';

export interface BidPayload {
  project_id?: string | null;
  vendor_id?: string | null;
  subcontractor_name?: string;
  bid_number?: string | null;
  amount?: number;
  status?: string;
  date_received?: string;
  valid_until?: string | null;
  payment_terms?: string | null;
  notes?: string | null;
  category?: string | null;
  other_category?: string | null;
  name?: string | null;
  description?: string | null;
  submitted_at?: string | null;
  accepted_at?: string | null;
  rejected_at?: string | null;
  /** the Bid Package the bid answers (the server takes the vendor and the project from the invitation) */
  bid_package_id?: string;
}

export type BidItemInput = Pick<BidItem, 'description' | 'unit' | 'quantity' | 'unit_price' | 'total_price'> & { category?: string | null; notes?: string | null };

/**
 * Bids data layer. Every read and write goes through the backend API (generic entity / lookups reads and the generic
 * entity API); the company and the author are never sent -- the server derives them from the session. The Bids TABLE
 * itself is the server-driven list (hooks/useEntityList.ts); this is for what a screen needs besides it.
 */
async function send<T = any>(path: string, method: 'POST' | 'PUT' | 'DELETE', body: unknown, failure: string): Promise<T> {
  const response = await fetch(getApiUrl(path), {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(parsed.error || parsed.message || failure);
  return parsed.data as T;
}

const toItemRow = (bidId: string, item: BidItemInput) => ({
  bid_id: bidId,
  description: item.description,
  unit: item.unit,
  quantity: item.quantity ?? 0,
  unit_price: item.unit_price ?? 0,
  total_price: item.total_price ?? 0,
  category: item.category ?? null,
  notes: item.notes ?? null,
});

export const bidApiService = {
  getProjects(): Promise<Project[]> {
    return fetchProjectLookup({ includeInactive: true }).then((projects) => projects.map(({ id, name }) => ({ id, name })));
  },

  /** One bid with its items and its project. */
  async getBid(bidId: string): Promise<any> {
    const [bid, items] = await Promise.all([
      fetchEntity<any>('bid', bidId),
      fetchEntityList<any>('bid_item', { filters: { bid_id: bidId } }),
    ]);
    return { ...bid, bid_items: items };
  },

  createBid(bid: BidPayload): Promise<{ id: string } & Record<string, any>> {
    return send('api/entities/bid', 'POST', bid, 'Failed to create bid');
  },

  updateBid(bidId: string, updates: BidPayload): Promise<any> {
    return send(`api/entities/bid/${bidId}`, 'PUT', updates, 'Failed to update bid');
  },

  async deleteBid(bidId: string): Promise<void> {
    await send(`api/entities/bid/${bidId}`, 'DELETE', {}, 'Failed to delete bid');
  },

  /** All of a bid's items in ONE request. */
  async addBidItems(bidId: string, items: BidItemInput[]): Promise<void> {
    if (items.length === 0) return;
    await send('api/entities/bid_item/bulk', 'POST', { items: items.map((item) => toItemRow(bidId, item)) }, 'Failed to save bid items');
  },

  /** Replaces a bid's items with `items` in ONE request (the server writes the new ones before removing the old ones). */
  async replaceBidItems(bidId: string, items: BidItemInput[]): Promise<void> {
    await send(`api/entities/bid_item/bulk/${bidId}`, 'PUT', { items: items.map((item) => toItemRow(bidId, item)) }, 'Failed to update bid items');
  },
};
